import React from 'react';

/**
 * QuickAction - Standardized action button for cockpit workflows
 * Provides consistent interaction patterns for common clinical actions
 */
interface QuickActionProps {
  title: string;
  description?: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

const QuickAction: React.FC<QuickActionProps> = ({
  title,
  description,
  icon,
  onClick,
  variant = 'secondary',
  size = 'md',
  disabled = false,
  loading = false,
  className = ''
}) => {
  const baseClasses = 'flex items-center p-3 text-left hover:bg-gray-50 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50';
  
  const variantClasses = {
    primary: 'bg-primary-50 border-2 border-primary-200 hover:bg-primary-100',
    secondary: 'bg-white border border-gray-200 hover:border-gray-300',
    outline: 'border-2 border-dashed border-gray-300 hover:border-gray-400'
  };

  const sizeClasses = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4'
  };

  const disabledClasses = disabled 
    ? 'opacity-50 cursor-not-allowed hover:bg-white' 
    : 'cursor-pointer';

  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${className}`;

  const handleClick = () => {
    if (!disabled && !loading) {
      onClick();
    }
  };

  return (
    <button
      type="button"
      className={`w-full ${buttonClasses}`}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-describedby={description ? `${title}-description` : undefined}
    >
      <div className="mr-3 text-primary-600 flex-shrink-0">
        {loading ? (
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent" />
        ) : (
          icon
        )}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">
          {title}
        </p>
        {description && (
          <p 
            id={`${title}-description`}
            className="text-xs text-gray-500 mt-1"
          >
            {description}
          </p>
        )}
      </div>
    </button>
  );
};

export default QuickAction;